import { createContext, useContext, useEffect, useState } from "react";
import { listRegions } from "../lib/api";

const StoreRegionContext = createContext(null);
const STORAGE_KEY = "my_store_region_v1";

const CURRENCY_SYMBOL = {
  "CA-ON": "C$",
  US: "$",
  UK: "£",
  EU: "€",
};

export function StoreRegionProvider({ children }) {
  const [regions, setRegions] = useState([]);
  const [region, setRegion] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || "CA-ON";
    } catch {
      return "CA-ON";
    }
  });

  useEffect(() => {
    listRegions().then(setRegions).catch(() => setRegions([]));
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, region);
    } catch {
      /* ignore */
    }
  }, [region]);

  const current = regions.find((r) => r.code === region) || {
    code: region,
    label: region,
    max_shipping_days: 7,
    currency: "USD",
  };

  const symbol = CURRENCY_SYMBOL[region] || "$";

  // Resolve price for a store product based on selected region.
  const priceFor = (product) => {
    if (!product) return 0;
    const map = product.prices_by_region || {};
    if (map[region] && map[region] > 0) return map[region];
    return product.price_usd || 0;
  };

  // Stock for region: null = unlimited.
  const stockFor = (product) => {
    if (!product) return null;
    const map = product.stock_by_region || {};
    const v = map[region];
    return v === null || v === undefined ? null : Number(v);
  };

  const stockState = (product) => {
    const s = stockFor(product);
    if (s === null) return { code: "in_stock", label: null };
    if (s === 0) return { code: "sold_out", label: "Sold Out" };
    if (s <= 3) return { code: "low", label: `Only ${s} left` };
    return { code: "in_stock", label: `${s} in stock` };
  };

  return (
    <StoreRegionContext.Provider
      value={{ region, setRegion, regions, current, symbol, priceFor, stockFor, stockState }}
    >
      {children}
    </StoreRegionContext.Provider>
  );
}

export function useStoreRegion() {
  const ctx = useContext(StoreRegionContext);
  if (!ctx) {
    return {
      region: "CA-ON",
      setRegion: () => {},
      regions: [],
      current: { code: "CA-ON", label: "Ontario, Canada", max_shipping_days: 7, currency: "CAD" },
      symbol: "C$",
      priceFor: (p) => (p?.price_usd ?? 0),
      stockFor: () => null,
      stockState: () => ({ code: "in_stock", label: null }),
    };
  }
  return ctx;
}
